/**
 * WKWebView bridge client. Posts versioned envelopes to
 * window.webkit.messageHandlers.swimset and validates the reply with Zod.
 *
 * Mirrors shared/contract/types.ts. Plan 03's Swift BridgeRouter must respond
 * with envelopes that satisfy BridgeResponseSchema in ./validate.ts.
 */
import { z } from "zod";
import type {
  BetaDiagnosticsResponse,
  BridgeRequest,
  ClubStatusResponse,
  ClubSnapshotResponse,
  CompanionFirehoseRequest,
  CompanionLapLabelsRequest,
  ClubSwimFirehoseRequest,
  ClubSwimFirehoseResponse,
  GoalPreferences,
  HandshakeResponse,
  LapLabel,
  ListAttendanceResponse,
  ListCompanionsResponse,
  ListWorkoutsResponse,
  MarkAttendanceRequest,
  MarkAttendanceResponse,
  RemoveCompanionRequest,
  RemoveCompanionResponse,
  SelectTabRequest,
  SelectTabResponse,
  SetClubNameResponse,
  SetGoalsResponse,
  SetLapLabelsResponse,
  SwimFirehoseRequest,
  SwimFirehoseResponse,
  TriggerHapticRequest,
  TriggerHapticResponse,
} from "@contract/types";
import {
  AttendanceEntrySchema,
  BridgeResponseSchema,
  ClubStatusResponseSchema,
  ClubSnapshotResponseSchema,
  CompanionSummarySchema,
  GetLapLabelsResponseSchema,
  GoalPreferencesSchema,
  HandshakeResponseSchema,
  LapLabelSchema,
  ClubSwimFirehoseResponseSchema,
  MarkAttendanceResponseSchema,
  RemoveCompanionResponseSchema,
  SelectTabResponseSchema,
  SetClubNameResponseSchema,
  SetGoalsResponseSchema,
  SetLapLabelsResponseSchema,
  SwimFirehoseResponseSchema,
  TriggerHapticResponseSchema,
  WorkoutSummarySchema,
  BetaDiagnosticsResponseSchema,
  SendFeedbackResponseSchema,
  OpenDebugToolsResponseSchema,
  RequestHealthAccessResponseSchema,
  OpenSettingsResponseSchema,
  LogResponseSchema,
} from "./validate";
import { generateStubFirehose, generateStubCompanionFirehose, shouldUseStubFirehose, companionExtraDaysBack } from "../lib/stubFirehose";
import { generateStubWorkouts } from "../lib/stubWorkouts";
import { unsetGoals } from "../lib/goalDefaults";

/** Stub-mode in-memory goals so the snapshot harness round-trips set→get. */
let stubGoals: GoalPreferences = unsetGoals();

/**
 * Stub-mode in-memory lap labels keyed by workoutUUID.
 * Sentinel key used when no UUID is provided so the absent-UUID bucket is
 * isolated from any real swim UUID.
 */
const STUB_NO_UUID_KEY = "__no_uuid__";
const stubLapLabelsMap = new Map<string, LapLabel[]>();

// Must equal LapLabelStore.schemaVersion on the native side. Bump BOTH when
// the label schema changes; native rejects writes from older clients.
export const LABEL_SCHEMA_VERSION = 1;

/** TEST-ONLY: clear the stub map between test cases. */
export function _clearStubLapLabels(): void {
  stubLapLabelsMap.clear();
}

interface WebkitHandler {
  postMessage(envelope: BridgeRequest): Promise<unknown>;
}

interface SwimsetWebkit {
  messageHandlers: { swimset?: WebkitHandler };
}

/**
 * Window augmentation:
 * - `webkit` is the WKWebView native bridge handle (injected by iOS).
 * - `__SWIMSET_FIXTURE__` is the Playwright/snapshot escape hatch — the
 *   harness injects a real-swim fixture via `page.addInitScript(...)` so
 *   prototypes render against actual data instead of the stub. Fixture
 *   takes precedence over both the stub flag and the live bridge.
 * - `__SWIMSET_FIXTURES__` (plural) is the MULTI-swim escape hatch — an array
 *   of real firehose responses. When present, `listWorkouts()` derives one
 *   WorkoutSummary per fixture from each fixture's OWN `workout` metadata, and
 *   `getWorkoutFirehose({workoutUUID})` returns the entry whose workout uuid
 *   matches. This lets the Sets-Discovered history scan fold SEVERAL real swims
 *   so the ledger renders varied real counts/months in a snapshot (the single
 *   `__SWIMSET_FIXTURE__` path only ever yields a one-row ledger). Both globals
 *   are DEV-ONLY snapshot seams: every branch below is gated on the global being
 *   present, so PRODUCTION (no global injected) behavior is byte-for-byte
 *   unchanged.
 */
declare global {
  interface Window {
    webkit?: SwimsetWebkit;
    __SWIMSET_FIXTURE__?: SwimFirehoseResponse;
    __SWIMSET_FIXTURES__?: SwimFirehoseResponse[];
    /** DEV-ONLY seam for the screenshot harness: inject per-swim stroke stats
     *  to control StrokeTrendCard state (healthy vs below-floor) without touching
     *  the strokeStats.v1 cache. Never set in production. */
    __SWIMSET_STROKE_SAMPLE__?: { stats: import("../lib/strokeTrend").StrokeWorkoutStats[]; total: number };
  }
}

function injectedFixture(): SwimFirehoseResponse | null {
  if (typeof window === "undefined") return null;
  return window.__SWIMSET_FIXTURE__ ?? null;
}

/**
 * The plural multi-swim seam, or null when not injected. Inert in production —
 * the array is only ever set by the snapshot harness via addInitScript.
 */
function injectedFixtures(): SwimFirehoseResponse[] | null {
  if (typeof window === "undefined") return null;
  const fx = window.__SWIMSET_FIXTURES__;
  return Array.isArray(fx) && fx.length > 0 ? fx : null;
}

export class BridgeUnavailableError extends Error {
  constructor(
    public readonly reason: "no-handler" | "timeout" | "rejected",
    message: string,
  ) {
    super(message);
    this.name = "BridgeUnavailableError";
  }
}

const TIMEOUT_MS = 5000;

/**
 * Per-UUID firehose cache. Tapping back to a swim you already viewed reuses
 * the ~170KB payload instead of round-tripping the bridge. Only caches calls
 * with an explicit `workoutUUID`; the "most recent swim" (no UUID) path
 * always re-fetches so newly-recorded workouts surface.
 *
 * Bounded in practice by the native `listWorkouts(limit:50)` ceiling —
 * worst-case ~8.5MB resident, acceptable for WKWebView.
 */
const firehoseCache = new Map<string, SwimFirehoseResponse>();

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** TRUE only when the real native shell's message handler exists. Unlike
 *  isAvailable(), stub mode and injected fixtures do NOT count — use this to
 *  branch "device vs browser" without stub/fixture modes masquerading as
 *  native (they'd regress dev-browser + snapshot renders). */
export function hasNativeShell(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.webkit?.messageHandlers?.swimset?.postMessage === "function"
  );
}

/**
 * No shell to ask, but simulated swim data to show: the public showcase (main.ts
 * injects the published swim), a snapshot harness's injected fixture(s), or the
 * `?firehoseStub=1` stub. A read with a local answer gives it here instead of
 * reaching call(), whose handler does not exist. With a shell present (the app,
 * or a harness's fake bridge) every read still goes to the shell.
 */
function simulatedShell(): boolean {
  return !hasNativeShell() && (injectedFixture() !== null || injectedFixtures() !== null || shouldUseStubFirehose());
}

function isAvailable(): boolean {
  // Injected fixture(s) or stub mode make the bridge "available" in a desktop
  // browser so the Prototypes view renders against simulated data.
  if (injectedFixture()) return true;
  if (injectedFixtures()) return true;
  if (shouldUseStubFirehose()) return true;
  return hasNativeShell();
}

async function call<T>(
  method: string,
  args: Record<string, unknown>,
  schema: z.ZodType<T>,
  { timeoutMs = TIMEOUT_MS }: { timeoutMs?: number } = {},
): Promise<T> {
  if (!isAvailable()) {
    throw new BridgeUnavailableError(
      "no-handler",
      `Bridge handler "swimset" not registered`,
    );
  }
  const envelope: BridgeRequest = { v: 1, method, args, id: newId() };
  const handler = window.webkit!.messageHandlers.swimset!;

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        new BridgeUnavailableError(
          "timeout",
          `Bridge call ${method} timed out after ${timeoutMs}ms`,
        ),
      );
    }, timeoutMs);
  });

  let raw: unknown;
  try {
    raw = await Promise.race([handler.postMessage(envelope), timeoutPromise]);
  } catch (err) {
    if (err instanceof BridgeUnavailableError) throw err;
    throw new BridgeUnavailableError(
      "rejected",
      err instanceof Error ? err.message : String(err),
    );
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }

  const parsed = BridgeResponseSchema(schema).safeParse(raw);
  if (!parsed.success) {
    throw new BridgeUnavailableError(
      "rejected",
      `Envelope validation failed: ${parsed.error.message}`,
    );
  }
  const env = parsed.data;
  if (!env.ok) {
    throw new BridgeUnavailableError(
      "rejected",
      `${env.error.code}: ${env.error.message}`,
    );
  }
  return env.data as T;
}

/**
 * A list reply the club reads, validated row by row (build 11 resilience audit, B-017):
 * the reply must still be an array, but a row that fails its schema is dropped and named
 * in one warn line instead of rejecting the call. Before this one bad companion, workout
 * summary, attendance row or clubmate label blanked the whole Club roster or day card.
 * NOT for the viewer's own lap labels: a write replaces the whole list, so a dropped row
 * would be erased by the next save; `getLapLabels` stays strict (C2a refuses the write).
 */
async function callRows<T>(method: string, args: Record<string, unknown>, row: z.ZodType<T>): Promise<T[]> {
  const rows = await call(method, args, z.array(z.unknown()));
  const kept: T[] = [];
  rows.forEach((raw, i) => {
    const parsed = row.safeParse(raw);
    if (parsed.success) kept.push(parsed.data);
    else warnToNative(`[bridge] ${method} dropped row ${i} of ${rows.length} field=${String(parsed.error.issues[0]?.path[0] ?? "")}`);
  });
  return kept;
}

/**
 * A club drop warning that must reach the cable (build 12, B-017 follow-up): the
 * console.warn stays, and on a native shell the same line goes to the `log` method,
 * which writes it to the unified log. Fire-and-forget: an older shell answers
 * BRIDGE_METHOD_UNKNOWN and a failed call is swallowed, never warned again (a warn
 * about a warn would loop). Callers pass ids and counts only, never names or swims.
 */
export function warnToNative(message: string, ...detail: unknown[]): void {
  console.warn(message, ...detail);
  if (!hasNativeShell()) return;
  const line = detail.length > 0 ? `${message} ${detail.map((d) => (d instanceof Error ? d.message : String(d))).join(" ")}` : message;
  call("log", { level: "warn", message: line }, LogResponseSchema).catch(() => {});
}

export interface BridgeClient {
  isAvailable(): boolean;
  handshake(): Promise<HandshakeResponse>;
  listWorkouts(args?: {
    limit?: number;
    activityType?: string;
  }): Promise<ListWorkoutsResponse>;
  getWorkoutFirehose(args?: SwimFirehoseRequest): Promise<SwimFirehoseResponse | null>;
  getGoals(): Promise<GoalPreferences>;
  setGoals(prefs: GoalPreferences): Promise<SetGoalsResponse>;
  getLapLabels(workoutUUID?: string): Promise<LapLabel[]>;
  setLapLabels(labels: LapLabel[], workoutUUID?: string): Promise<SetLapLabelsResponse>;
  triggerHaptic(style: TriggerHapticRequest["style"]): Promise<TriggerHapticResponse>;
  /** Open the native lake-route map for a swim (open water only). Fire-and-open:
   *  native presents a full-screen MapKit route screen OUTSIDE the web view. The
   *  result envelope isn't read by the caller — only that the call resolved.
   *  Method-gated: callers must verify `supportsMethod("openRouteMap")` first so
   *  this is never invoked on a shell that can't present the map. */
  openRouteMap(workoutUUID: string): Promise<unknown>;
  listCompanions(): Promise<ListCompanionsResponse>;
  getCompanionFirehose(
    args: CompanionFirehoseRequest,
  ): Promise<SwimFirehoseResponse | null>;
  /** A clubmate's saved stroke corrections for one of their swims (LapLabels
   *  record, stroke-sync). [] when they published none. Method-gated: verify
   *  `supportsMethod("getCompanionLapLabels")` first. */
  getCompanionLapLabels(args: CompanionLapLabelsRequest): Promise<LapLabel[]>;
  /** Build 16 (parity): ANY club swim's firehose + projected labels by UUID, own (any age) or a
   *  clubmate's stored one. `{ok:false, reason}` when this phone holds none or a read failed.
   *  Method-gated: verify `supportsMethod("getClubSwimFirehose")` first. */
  getClubSwimFirehose(args: ClubSwimFirehoseRequest): Promise<ClubSwimFirehoseResponse>;
  removeCompanion(
    args: RemoveCompanionRequest,
  ): Promise<RemoveCompanionResponse>;
  /** Write the viewer's own "I was there" for a local day key ("YYYY-MM-DD",
   *  clubSessions.localDayKey). Resolves { ok:false } when native could not
   *  CONFIRM the CloudKit save (offline, cold-boot role unknown, quota) — the
   *  caller renders retry copy, never a phantom roster row. Method-gated:
   *  callers must verify `supportsMethod("markAttendance")` first. */
  markAttendance(args: MarkAttendanceRequest): Promise<MarkAttendanceResponse>;
  /** Every member's tap-presence entries (all days), deduped by (creator, day).
   *  The local user appears as "__defaultOwner__" — bridgeClubSessionSource maps
   *  that token to its viewer id. */
  listAttendance(): Promise<ListAttendanceResponse>;
  /** Everything the ClubTab front door needs in one call: iCloud availability
   *  (native-authored plain-words `accountMessage` rendered verbatim), the
   *  local user's club role, publish opt-in, and the typed name (null → show
   *  the one-time name prompt). Method-gated: verify
   *  `supportsMethod("getClubStatus")` first. */
  getClubStatus(): Promise<ClubStatusResponse>;
  getClubSnapshot(): Promise<ClubSnapshotResponse>;
  /** Publish the typed club name. `{ok:false}` = invalid or the save was not
   *  confirmed (offline, quota) — keep the prompt, show retry copy. */
  setClubName(name: string): Promise<SetClubNameResponse>;
  /** Open the NATIVE club settings sheet (opt-in, invites, account guard) over
   *  the web view. Fire-and-present, openRouteMap parity. Method-gated. */
  openClubSettings(): Promise<unknown>;
  /** Ask the shell to select a tab. Only meaningful under the native tab bar,
   *  where the target tab is a DIFFERENT web view and a `currentTab` store write
   *  would land in the wrong one. `workoutUUID` rides along for the Scores->Swim
   *  jump: the shell selects Swim and dispatches `swimset:select-swim` into that
   *  view. Method-gated — callers must verify `supportsMethod("selectTab")` and
   *  fall back to the store, which is what keeps this bundle working in a plain
   *  browser and on an older shell. */
  selectTab(args: SelectTabRequest): Promise<SelectTabResponse>;
  /** Beta panel: build, device, HealthKit, iCloud and club status for the
   *  setup check. No names, no swim content. Method-gated:
   *  `supportsMethod("betaDiagnostics")`. */
  betaDiagnostics(): Promise<BetaDiagnosticsResponse>;
  /** Beta panel: the native Mail composer to support with `body` prefilled, or
   *  the share sheet when Mail is not set up. Method-gated. */
  sendFeedback(body: string): Promise<{ presented: "mail" | "share" }>;
  /** Beta panel "Turn on": ask HealthKit for access, get the fresh state. Method-gated. */
  requestHealthAccess(): Promise<{ health: "allowed" | "not allowed" | "not asked" }>;
  /** Beta panel "Settings": this app's page in iOS Settings. Method-gated. */
  openSettings(): Promise<{ ok: boolean }>;
  /** DEBUG shells only: the developer sheet. Method-gated. */
  openDebugTools(): Promise<{ ok: boolean }>;
  /** One line to the native unified log. Use `warnToNative`, which keeps the console.warn. */
  log(level: string, message: string): Promise<{ ok: boolean }>;
}

export const bridge: BridgeClient = {
  isAvailable,
  handshake(): Promise<HandshakeResponse> {
    // Stub, snapshot harness or public showcase: no shell to ask. Without this the
    // stub load threw here, offline read "rejected" and every still carried the
    // "saved copy" pill a phone with Health granted never shows (VP round 1, B2);
    // on the showcase the workouts store read the throw as "Swims unavailable".
    // No methods listed, so every method-gated affordance stays hidden, as before.
    if (simulatedShell()) {
      return Promise.resolve({ nativeApiVersion: 1, shellVersion: "stub", iosVersion: "stub", commitSha: "stub", supportedMethods: [] });
    }
    return call("handshake", {}, HandshakeResponseSchema);
  },
  listWorkouts(
    args: { limit?: number; activityType?: string } = {},
  ): Promise<ListWorkoutsResponse> {
    // DEV-ONLY multi-swim seam: derive one summary per injected fixture from
    // that fixture's own workout metadata (recency-sorted, newest first). Lets
    // the Sets-Discovered history scan see SEVERAL real swims so the ledger
    // renders varied counts/months. Inert without the global → no prod impact.
    // The showcase's single injected swim is a one-swim list.
    const single = injectedFixture();
    const fixtures = injectedFixtures() ?? (single && simulatedShell() ? [single] : null);
    if (fixtures) {
      const limit = typeof args.limit === "number" ? args.limit : 50;
      const summaries: ListWorkoutsResponse = fixtures
        .map((fh) => fh.workout)
        .filter((w) => !args.activityType || w.activityType === args.activityType)
        .sort(
          (a, b) =>
            new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
        )
        .slice(0, limit);
      return Promise.resolve(summaries);
    }
    if (shouldUseStubFirehose()) {
      // Same query-param flag drives both firehose AND workout-list stubs so
      // Set-tab + MetricRings render against believable data in the snapshot
      // harness. Real bridge mode unaffected.
      const limit = typeof args.limit === "number" ? args.limit : 50;
      return Promise.resolve(generateStubWorkouts().slice(0, limit));
    }
    return callRows("listWorkouts", args as Record<string, unknown>, WorkoutSummarySchema);
  },
  getWorkoutFirehose(
    args: SwimFirehoseRequest = {},
  ): Promise<SwimFirehoseResponse | null> {
    const fix = injectedFixture();
    if (fix) return Promise.resolve(fix);
    // DEV-ONLY multi-swim seam: serve the entry whose workout uuid matches the
    // requested UUID; with no UUID ("most recent swim"), serve the newest. Same
    // gating as listWorkouts above — inert without the global.
    const fixtures = injectedFixtures();
    if (fixtures) {
      if (args.workoutUUID) {
        const match = fixtures.find((fh) => fh.workout.uuid === args.workoutUUID);
        return Promise.resolve(match ?? null);
      }
      const newest = [...fixtures].sort(
        (a, b) =>
          new Date(b.workout.startDate).getTime() -
          new Date(a.workout.startDate).getTime(),
      )[0];
      return Promise.resolve(newest ?? null);
    }
    if (shouldUseStubFirehose()) {
      return Promise.resolve(generateStubFirehose());
    }
    if (args.workoutUUID && firehoseCache.has(args.workoutUUID)) {
      return Promise.resolve(firehoseCache.get(args.workoutUUID)!);
    }
    return call(
      "getWorkoutFirehose",
      args as Record<string, unknown>,
      SwimFirehoseResponseSchema.nullable(),
    ).then((res) => {
      if (res && args.workoutUUID) firehoseCache.set(args.workoutUUID, res);
      return res;
    });
  },
  getGoals(): Promise<GoalPreferences> {
    if (shouldUseStubFirehose()) {
      return Promise.resolve(stubGoals);
    }
    return call("getGoals", {}, GoalPreferencesSchema);
  },
  setGoals(prefs: GoalPreferences): Promise<SetGoalsResponse> {
    if (shouldUseStubFirehose()) {
      stubGoals = { ...prefs, updatedAt: new Date().toISOString() };
      return Promise.resolve({ prefs: stubGoals });
    }
    return call("setGoals", { prefs }, SetGoalsResponseSchema);
  },
  getLapLabels(workoutUUID?: string): Promise<LapLabel[]> {
    if (shouldUseStubFirehose()) {
      const key = workoutUUID ?? STUB_NO_UUID_KEY;
      return Promise.resolve(stubLapLabelsMap.get(key) ?? []);
    }
    // An injected swim has no saved corrections (the published showcase swim
    // carries none). Without this the label read threw four times through its
    // retry and the showcase drew the swim only after 5.9 s.
    if (simulatedShell()) return Promise.resolve([]);
    return call("getLapLabels", workoutUUID ? { workoutUUID } : {}, GetLapLabelsResponseSchema);
  },
  setLapLabels(labels: LapLabel[], workoutUUID?: string): Promise<SetLapLabelsResponse> {
    if (shouldUseStubFirehose()) {
      const key = workoutUUID ?? STUB_NO_UUID_KEY;
      stubLapLabelsMap.set(key, labels);
      return Promise.resolve({ labels });
    }
    return call(
      "setLapLabels",
      workoutUUID
        ? { workoutUUID, clientLabelSchemaVersion: LABEL_SCHEMA_VERSION, labels }
        : { clientLabelSchemaVersion: LABEL_SCHEMA_VERSION, labels },
      SetLapLabelsResponseSchema,
    );
  },
  triggerHaptic(
    style: TriggerHapticRequest["style"],
  ): Promise<TriggerHapticResponse> {
    // Stub / no-handler mode: silent no-op. Snapshot harness and desktop
    // browser callers never feel a missing haptic; native silently swallows.
    if (!isAvailable()) {
      return Promise.resolve({ ok: true });
    }
    if (shouldUseStubFirehose()) {
      return Promise.resolve({ ok: true });
    }
    return call("triggerHaptic", { style }, TriggerHapticResponseSchema);
  },
  openRouteMap(workoutUUID: string): Promise<unknown> {
    // Stub / harness: no native map to present — resolve as a no-op so the
    // screenshot context never dead-ends. Production gates on
    // supportsMethod("openRouteMap") before calling, so this only reaches a real
    // shell that registered the method. Permissive result schema (z.unknown())
    // keeps the call independent of the concurrently-generated validate schema.
    if (shouldUseStubFirehose() || !isAvailable()) {
      return Promise.resolve({ ok: true });
    }
    return call("openRouteMap", { workoutUUID }, z.unknown());
  },
  listCompanions(): Promise<ListCompanionsResponse> {
    // Stub mode: one companion so the Club overlay chart renders in desktop-
    // browser screenshot QA without the native bridge. Summary fields are
    // derived from the companion's stub workout so they stay honest.
    if (shouldUseStubFirehose()) {
      // `stubCompanion=lastMonth` shifts the companion's swim onto an earlier day so
      // the different-day "no lap race today" state is screenshot-able (UAT 2026-06-08).
      const companion = generateStubCompanionFirehose({
        extraDaysBack: companionExtraDaysBack(),
      });
      return Promise.resolve([
        {
          originId: "STUB-COMPANION-ORIGINID",
          swimmerLabel: "Lane Buddy",
          workoutCount: 1,
          lastSwimDate: companion.workout.startDate,
          schemaVersion: 1,
        },
      ]);
    }
    return callRows("listCompanions", {}, CompanionSummarySchema);
  },
  getCompanionFirehose(
    args: CompanionFirehoseRequest,
  ): Promise<SwimFirehoseResponse | null> {
    if (shouldUseStubFirehose()) {
      return Promise.resolve(
        generateStubCompanionFirehose({ extraDaysBack: companionExtraDaysBack() }),
      );
    }
    return call(
      "getCompanionFirehose",
      args as unknown as Record<string, unknown>,
      SwimFirehoseResponseSchema.nullable(),
    );
  },
  getCompanionLapLabels(args: CompanionLapLabelsRequest): Promise<LapLabel[]> {
    // Stub / harness: the stub companion has no published corrections.
    if (shouldUseStubFirehose()) return Promise.resolve([]);
    return callRows("getCompanionLapLabels", args as unknown as Record<string, unknown>, LapLabelSchema);
  },
  getClubSwimFirehose(args: ClubSwimFirehoseRequest): Promise<ClubSwimFirehoseResponse> {
    return call("getClubSwimFirehose", args as unknown as Record<string, unknown>, ClubSwimFirehoseResponseSchema);
  },
  removeCompanion(
    args: RemoveCompanionRequest,
  ): Promise<RemoveCompanionResponse> {
    if (shouldUseStubFirehose()) {
      return Promise.resolve({ ok: true, removed: false });
    }
    return call(
      "removeCompanion",
      args as unknown as Record<string, unknown>,
      RemoveCompanionResponseSchema,
    );
  },
  markAttendance(args: MarkAttendanceRequest): Promise<MarkAttendanceResponse> {
    // Stub / harness: pretend-confirm so a screenshot context never dead-ends.
    // Production callers are supportsMethod-gated, so this never masks a real shell.
    if (shouldUseStubFirehose()) {
      return Promise.resolve({ ok: true });
    }
    // 30s, not the default 5s (audit rev-3 IMPORTANT): native markAttendance awaits
    // two sequential CloudKit network ops and can exceed 5s on slow cell. A save
    // that lands at 8s must not read to the user as a failure — see Task 8 UAT note.
    return call(
      "markAttendance",
      args as unknown as Record<string, unknown>,
      MarkAttendanceResponseSchema,
      { timeoutMs: 30_000 },
    );
  },
  listAttendance(): Promise<ListAttendanceResponse> {
    // Stub mode: no attendance — the fixture SOURCE carries the browser-render
    // attendee (clubSessionSource.ts), not the bridge stub.
    if (shouldUseStubFirehose()) {
      return Promise.resolve([]);
    }
    return callRows("listAttendance", {}, AttendanceEntrySchema);
  },
  getClubSnapshot(): Promise<ClubSnapshotResponse> {
    return call("getClubSnapshot", {}, ClubSnapshotResponseSchema);
  },
  getClubStatus(): Promise<ClubStatusResponse> {
    // Stub / harness: a healthy named member so the front door never blocks a
    // screenshot render (no banner, no prompt).
    if (shouldUseStubFirehose()) {
      return Promise.resolve({
        iCloudOk: true,
        accountMessage: null,
        role: "member",
        optedIn: true,
        myName: "You",
      });
    }
    // 15s: native runs an account check plus up to two zone enumerations —
    // slow cell must not read as a missing club (markAttendance precedent).
    return call("getClubStatus", {}, ClubStatusResponseSchema, { timeoutMs: 15_000 });
  },
  setClubName(name: string): Promise<SetClubNameResponse> {
    if (shouldUseStubFirehose()) {
      return Promise.resolve({ ok: true, name });
    }
    // 30s, markAttendance parity: the confirmed CloudKit save can exceed 5s.
    return call("setClubName", { name }, SetClubNameResponseSchema, { timeoutMs: 30_000 });
  },
  openClubSettings(): Promise<unknown> {
    // Stub / no-handler: no native sheet to present — resolve as a no-op so a
    // browser render never dead-ends. Production callers are supportsMethod-gated.
    if (shouldUseStubFirehose() || !isAvailable()) {
      return Promise.resolve({ ok: true });
    }
    return call("openClubSettings", {}, z.unknown());
  },
  selectTab(args: SelectTabRequest): Promise<SelectTabResponse> {
    // No shell, no tabs to select: report ok:false so the caller falls back to
    // its own `currentTab` store instead of silently going nowhere.
    if (!hasNativeShell()) {
      return Promise.resolve({ ok: false });
    }
    return call(
      "selectTab",
      { tab: args.tab, ...(args.workoutUUID ? { workoutUUID: args.workoutUUID } : {}) },
      SelectTabResponseSchema,
    );
  },
  betaDiagnostics(): Promise<BetaDiagnosticsResponse> {
    // 15s, getClubStatus parity: an account check plus up to two zone enumerations.
    return call("betaDiagnostics", {}, BetaDiagnosticsResponseSchema, { timeoutMs: 15_000 });
  },
  sendFeedback(body: string): Promise<{ presented: "mail" | "share" }> {
    return call("sendFeedback", { body }, SendFeedbackResponseSchema);
  },
  requestHealthAccess(): Promise<{ health: "allowed" | "not allowed" | "not asked" }> {
    // No timeout pressure from a human answering the iOS sheet: 60s.
    return call("requestHealthAccess", {}, RequestHealthAccessResponseSchema, { timeoutMs: 60_000 });
  },
  openSettings(): Promise<{ ok: boolean }> {
    return call("openSettings", {}, OpenSettingsResponseSchema);
  },
  openDebugTools(): Promise<{ ok: boolean }> {
    return call("openDebugTools", {}, OpenDebugToolsResponseSchema);
  },
  log(level: string, message: string): Promise<{ ok: boolean }> {
    return call("log", { level, message }, LogResponseSchema);
  },
};
